import i18next from 'i18next';
import { DateTime } from 'luxon';

import { assertNever } from './assert';

export type SectionName =
  | 'future'
  | 'today'
  | 'yesterday'
  | 'lastWeek'
  | 'thisMonth'
  | 'thisYear'
  | 'lastYear'
  | 'before'
  | 'unknown';

type Section = {
  name: SectionName;
  date: DateTime;
};

export type SectionInfo = {
  sectionName: SectionName;
  sectionLabel: string;
  isSectionStart: boolean;
};

const TIME_NOW = DateTime.local();

const DATE_SECTIONS: Section[] = [
  { name: 'future', date: TIME_NOW.startOf('day').plus({ days: 1 }) },
  { name: 'today', date: TIME_NOW.startOf('day') },
  { name: 'yesterday', date: TIME_NOW.startOf('day').minus({ days: 1 }) },
  { name: 'lastWeek', date: TIME_NOW.startOf('week').minus({ weeks: 1 }) },
  { name: 'thisMonth', date: TIME_NOW.startOf('month') },
  { name: 'thisYear', date: TIME_NOW.startOf('year') },
  { name: 'lastYear', date: TIME_NOW.startOf('year').minus({ years: 1 }) },
];

const DEFAULT_AVAILABLE_DATE_SECTIONS: SectionName[] = [
  'future',
  'thisMonth',
  'thisYear',
  'lastYear',
  'before',
  'unknown',
];

export type MarkSectionsByDateOptions<Item, Result> = {
  items: Item[];
  getDate: (item: Item) => string;
  mapFn: (item: Item, info: SectionInfo) => Result;
  availableDateSections?: SectionName[];
  /**
   * If `true`, marks the section with the day name, but only for past days within
   * the current week. Excludes today, yesterday, and any future dates.
   */
  markWithDayNameForPastDaysThisWeek?: boolean;
};

export function markSectionsByDate<Item, Result>(
  options: MarkSectionsByDateOptions<Item, Result>
): Result[] {
  const {
    items,
    getDate,
    mapFn,
    availableDateSections = DEFAULT_AVAILABLE_DATE_SECTIONS,
    markWithDayNameForPastDaysThisWeek = false,
  } = options;

  const sections = DATE_SECTIONS.filter((section) =>
    availableDateSections.includes(section.name)
  );

  let lastSectionLabel: string | null = null;

  const today = DateTime.now();
  const yesterdayNumber = today.minus({ day: 1 }).weekday;
  const currentWeekNumber = today.startOf('week').weekNumber;

  return items.map((item) => {
    let sectionName: SectionName;
    let sectionLabel: string;
    const date = DateTime.fromISO(getDate(item));

    if (
      date.weekNumber === currentWeekNumber &&
      date.weekday < yesterdayNumber &&
      markWithDayNameForPastDaysThisWeek
    ) {
      sectionName = 'unknown';
      sectionLabel = date.weekdayLong;
    } else {
      sectionName = getSectionName(date, sections);
      sectionLabel = getSectionLabel(date, sectionName);
    }

    let isSectionStart = false;

    if (lastSectionLabel !== sectionLabel) {
      lastSectionLabel = sectionLabel;
      isSectionStart = true;
    }

    return mapFn(item, {
      sectionName: sectionName,
      sectionLabel: sectionLabel,
      isSectionStart: isSectionStart,
    });
  });
}

function getSectionName(date: DateTime, groups: Section[]): SectionName {
  if (!date.isValid) {
    return 'unknown';
  }

  for (const group of groups) {
    if (date >= group.date) {
      return group.name;
    }
  }

  return 'before';
}

function getSectionLabel(date: DateTime, name: SectionName): string {
  switch (name) {
    case 'future':
      return i18next.t('DATE_TIME.future');
    case 'today':
      return i18next.t('DATE_TIME.today');
    case 'yesterday':
      return i18next.t('DATE_TIME.yesterday');
    case 'lastWeek':
      return i18next.t('DATE_TIME.lastWeek');
    case 'thisMonth':
      return i18next.t('DATE_TIME.thisMonth');
    case 'thisYear':
      return i18next.t('DATE_TIME.thisYear');
    case 'lastYear':
    case 'before':
      return date.toFormat('yyyy');
    case 'unknown':
      return i18next.t('DATE_TIME.unknown');
    default:
      assertNever(name, `Unknown date group "${name}"`);
  }
}
